import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useGenerateInvite } from '@/hooks/useGroupInvites';
import { copyToClipboard } from '@/lib/utils';
import { toast } from 'sonner';
import { T, RADIUS, SHADOW } from '@/lib/tokens';
import { INVITE_STEP_SKIPPED_KEY, getAppUrl } from '@/lib/constants';
import logoOnWhite from '@/assets/logo-on-white.svg';
import posthog from '@/lib/posthog';

interface OnboardingInviteState {
  groupId: string;
  groupName: string;
  addedItemCount?: number;
}

export default function OnboardingInvite() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const { groupId, groupName, addedItemCount } = (location.state as OnboardingInviteState) || {};

  const generateInvite = useGenerateInvite();
  const [inviteLink, setInviteLink] = useState('');
  const [message, setMessage] = useState('');
  const [copied, setCopied] = useState(false);
  const [hasCopied, setHasCopied] = useState(false);

  // Redirect if no group context (e.g. direct navigation)
  useEffect(() => {
    if (!groupId) {
      navigate('/onboarding/create-group', { replace: true });
    }
  }, [groupId, navigate]);

  // Generate invite on mount
  useEffect(() => {
    if (!groupId || inviteLink) return;
    generateInvite
      .mutateAsync({ groupId })
      .then((invite) => {
        const link = `${getAppUrl()}/invite/${invite.invite_code}`;
        setInviteLink(link);

        // Dynamic message referencing items added if available
        if (addedItemCount && addedItemCount > 0) {
          setMessage(
            `I've set up a sharing group on Share Shack and added ${addedItemCount} ${addedItemCount === 1 ? 'item' : 'items'} — join to see what's available and add your own: ${link}`,
          );
        } else {
          setMessage(
            `I'm setting up a group to keep track of stuff we lend each other. Join here: ${link}`,
          );
        }
      })
      .catch(() => {
        // Error toast handled by useGenerateInvite onError
      });
  }, [groupId]);

  const markOnboardingComplete = async () => {
    if (user) {
      await supabase
        .from('profiles')
        .update({ has_completed_onboarding: true } as any)
        .eq('user_id', user.id);
      queryClient.invalidateQueries({ queryKey: ['profile-onboarding'] });
    }
  };

  const handleCopy = async () => {
    await copyToClipboard(message);
    setCopied(true);
    setHasCopied(true);
    posthog.capture('onboarding_invite_copied', { group_id: groupId });
    toast.success('Copied to clipboard!');
    setTimeout(() => setCopied(false), 2000);
  };

  const handleSkip = async () => {
    localStorage.setItem(`${INVITE_STEP_SKIPPED_KEY}_${groupId}`, 'true');
    posthog.capture('onboarding_invite_skipped', { group_id: groupId });
    await markOnboardingComplete();
    posthog.capture('onboarding_completed', { group_id: groupId, invited: false });
    navigate(`/groups/${groupId}`);
  };

  const handleContinue = async () => {
    await markOnboardingComplete();
    posthog.capture('onboarding_completed', { group_id: groupId, invited: true });
    navigate(`/groups/${groupId}`);
  };

  if (!groupId) return null;

  return (
    <div
      style={{
        minHeight: '100vh',
        background: T.bg,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 20,
      }}
    >
      <div style={{ width: '100%', maxWidth: 420, textAlign: 'center' }}>
        {/* Logo */}
        <div style={{ marginBottom: 24 }}>
          <img
            src={logoOnWhite}
            alt="Share Shack"
            style={{ width: 36, height: 36, display: 'block', margin: '0 auto' }}
          />
          <div style={{ fontSize: 15, fontWeight: 600, color: T.text, marginTop: 4 }}>
            Share Shack
          </div>
        </div>

        {/* Step indicator dots — step 3 of 3 */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 6,
            marginBottom: 24,
          }}
        >
          {[1, 2, 3].map((s) => (
            <div
              key={s}
              style={{
                height: 8,
                width: s === 3 ? 32 : 8,
                borderRadius: 4,
                background: s === 3 ? T.accent : T.accentBorder,
                transition: 'all 0.2s',
              }}
            />
          ))}
        </div>

        <h1 style={{ fontSize: 22, fontWeight: 600, color: T.text, margin: '0 0 8px' }}>
          Invite your group
        </h1>
        <p style={{ fontSize: 14, color: T.textSec, lineHeight: 1.65, margin: '0 0 24px' }}>
          Share Shack works best with others. Send this link to your group.
        </p>

        {/* Message textarea */}
        <textarea
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          rows={4}
          style={{
            width: '100%',
            padding: '12px 14px',
            border: `1.5px solid ${T.border}`,
            borderRadius: RADIUS.md,
            background: T.surfaceSubtle,
            color: T.text,
            fontSize: 14,
            fontFamily: 'inherit',
            resize: 'vertical',
            outline: 'none',
            boxSizing: 'border-box',
            lineHeight: 1.55,
            marginBottom: 16,
          }}
          placeholder={generateInvite.isPending ? 'Generating invite link…' : ''}
          disabled={generateInvite.isPending}
        />

        {/* Copy CTA */}
        <button
          onClick={handleCopy}
          disabled={!inviteLink || generateInvite.isPending}
          style={{
            width: '100%',
            padding: '13px',
            borderRadius: RADIUS.md,
            background: !inviteLink || generateInvite.isPending ? T.surfaceSubtle : T.accent,
            color: !inviteLink || generateInvite.isPending ? T.textMuted : T.white,
            border: !inviteLink || generateInvite.isPending ? `1px solid ${T.border}` : 'none',
            fontSize: 15,
            fontWeight: 600,
            cursor: !inviteLink || generateInvite.isPending ? 'not-allowed' : 'pointer',
            fontFamily: 'inherit',
            boxShadow: !inviteLink || generateInvite.isPending ? 'none' : SHADOW.btn.accent,
            marginBottom: 12,
          }}
        >
          {copied ? '✓ Copied' : 'Copy Invite Link'}
        </button>

        {/* Continue button — appears after first copy */}
        {hasCopied && (
          <button
            onClick={handleContinue}
            style={{
              width: '100%',
              padding: '12px',
              borderRadius: RADIUS.md,
              background: T.surface,
              color: T.textSec,
              border: `1px solid ${T.border}`,
              fontSize: 14,
              fontWeight: 600,
              cursor: 'pointer',
              fontFamily: 'inherit',
              marginBottom: 12,
            }}
          >
            Continue to group
          </button>
        )}

        {/* Skip link */}
        <button
          onClick={handleSkip}
          style={{
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            color: T.textMuted,
            fontSize: 13,
            fontFamily: 'inherit',
            padding: '8px 0',
          }}
        >
          Skip for now
        </button>
      </div>
    </div>
  );
}
